import { useEffect, useState } from 'react';
import { logEvent } from '@/config/analytics';
import { buildRoomLink } from '@/config/app-config';
import type { Translator } from '@/i18n';
import { Icon } from './Icon';
import { copyInviteLink, shareInvite } from './invite-actions';

interface Props {
  t: Translator['t'];
  roomId: string;
}

/**
 * Картка запрошення в центрі порожньої кімнати, створеної в цій вкладці.
 *
 * Той самий зміст, що в мобільному клієнті: посилання, «Поділитися»,
 * «Копіювати» і рядок про браузер без встановлення. Від видимості кнопок
 * керування не залежить.
 */
export function InviteCard({ t, roomId }: Props): React.JSX.Element {
  const [sent, setSent] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const link = buildRoomLink(roomId);

  useEffect(() => {
    logEvent('invite_card_shown');
  }, [roomId]);

  return (
    <div
      className="invite-card"
      onClick={(event) => {
        // Дотик по картці не має ховати кнопки керування під нею.
        event.stopPropagation();
      }}
    >
      <h2 className="invite-card__title">{t('inviteCardTitle')}</h2>
      <p className="invite-card__body">{t('inviteCardBody')}</p>
      <p className="invite-card__link">{link.replace(/^https?:\/\//u, '')}</p>
      <button
        type="button"
        className="button button--primary invite-card__share"
        onClick={() => {
          void shareInvite(t, roomId, 'card').then((outcome) => {
            if (outcome === 'cancelled') return;
            if (outcome !== 'shared') setNotice(outcome);
            setSent(true);
          });
        }}
      >
        <Icon name="share" size={20} />
        {t('inviteShareLink')}
      </button>
      <button
        type="button"
        className="button invite-card__copy"
        onClick={() => {
          void copyInviteLink(t, roomId, 'card').then((message) => {
            setNotice(message);
            setSent(true);
          });
        }}
      >
        <Icon name="content_copy" size={18} />
        {t('inviteCopy')}
      </button>
      {notice !== null && <p className="invite__notice invite-card__notice">{notice}</p>}
      <p className="invite-card__hint">
        <Icon name="check_circle" size={16} />
        {sent ? t('inviteSentHint') : t('inviteBrowserHint')}
      </p>
    </div>
  );
}
